import React, { useMemo } from 'react'
import { FormTextField, FormPlaceSuggest, FormDateSuggest } from '@fs/zion-form'
import { IconButton, Row, Separator } from '@fs/zion-ui'
import { ContentDelete } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  countSupersOfSharedField,
  isDateField,
  isEditableField,
  isPlaceField,
  shouldLockSharedField,
  useSlsStateContext,
} from '@fs/zion-sls'
import DanglingFieldButton from './DanglingFieldButton'
import SharedFieldButton from './SharedFieldButton'
import HighlightButtonOld from './HighlightButtonOld'
import { getDateFields } from '../../../../../shared/utils/dateUtils'

/**
 * PersonFormElement component renders different input elements based on the provided field type.
 * It supports place suggestion, date suggestion, and text input fields, along with an optional
 * highlight button for editing.

 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.field - The field object containing information about the form field.
 * @param {function} [props.onFocus] - Function to handle the focus event of the input field.
 * @param {function} [props.onBlur] - Function to handle the blur event of the input field.
 * @param {function} [props.onDelete] - Function to handle the click event of the delete icon button. If not passed, the delete button will not be rendered.
 * @param {Object} [props.danglingButtonProps] - The field object containing props for the DanglingFieldButton component.
 * @param {function} [props.danglingButtonProps.onClick] - Function to handle the click event.
 * @param {Object} [props.sharedFieldButtonProps] - The field object containing props for the SharedFieldButton component.
 * @param {function} [props.sharedFieldButtonProps.onClick] - Function to handle the click event.
 * @param {boolean} [props.showEditHighlight=true] - Flag to determine whether to show the edit highlight button.
 * @param {boolean} [props.enableLockSharedField=true] - Flag to determine whether to enable the locking mechanism for shared fields.
 * 
 * @returns {JSX.Element} The rendered component.
 */
const PersonFormElement = ({
  field,
  onFocus,
  onBlur,
  onDelete,
  danglingButtonProps,
  sharedFieldButtonProps,
  showEditHighlight = true,
  enableLockSharedField = true,
  maxLength = 256,
  isDateReadOnly = false,
}) => {
  const state = useSlsStateContext()
  const { types } = state
  const [t] = useTranslation()
  const numberOfSharedSupers = countSupersOfSharedField(field, state.images)
  const dateFields = getDateFields(types)
  const isShared = enableLockSharedField && numberOfSharedSupers > 1 // This is simpler than looking at the field.superElements because there are floating NAME elements left over from when persons were marked vacant that shouldn't be counted.
  const isDangling = field.isDangling
  const isEditable = isEditableField(field)
  const isPlaceFieldOrDateField = useMemo(() => isPlaceField(field) || isDateField(field), [field])
  const isNonStandardDateField = useMemo(() => dateFields.includes(field.fieldType), [dateFields, field.fieldType])
  const commonProps = {
    'data-testid': field?.fieldType || field?.elementType,
    name: field.name || field.id,
    key: field.id,
    label: field.label,
    placeholder: field.label,
    onFocus,
    onBlur,
  }
  return (
    <>
      {!isShared && isDangling && <DanglingFieldButton field={field} {...danglingButtonProps} />}
      {isShared && (
        <SharedFieldButton
          field={field}
          readonly={shouldLockSharedField(field, state.images) || !isEditable}
          {...sharedFieldButtonProps}
        />
      )}
      {!isShared && !isDangling && (
        <>
          {isPlaceField(field) && <FormPlaceSuggest {...commonProps} standardize clearOnClickAway />}
          {isDateField(field) && (
            <FormDateSuggest {...commonProps} standardize clearOnClickAway disabled={isDateReadOnly} />
          )}
          {!isPlaceField(field) && !isDateField(field) && (
            <FormTextField {...commonProps} maxLength={maxLength} disabled={isDateReadOnly && isNonStandardDateField} />
          )}
          {isPlaceFieldOrDateField && <Separator size="xs" />}
          <Row>
            {showEditHighlight && <HighlightButtonOld field={field} onDelete={onDelete} growX />}
            {onDelete && !isDateReadOnly && (
              <IconButton
                Icon={ContentDelete}
                color="red"
                onClick={onDelete}
                title={t('edit-index.remove-field.button', 'Remove Field')}
                // eslint-disable-next-line no-unsafe-optional-chaining
                data-testid={`remove-${(field?.fieldType).toLowerCase()}-field`}
                linkName="Remove Field"
                dense
              />
            )}
          </Row>
        </>
      )}
    </>
  )
}

export default PersonFormElement
